// M8-ASSESS: the proposal translator (program §5 M8 "assessment_proposal translator (new)";
// x-ray.md §4 and §10: nothing in X-Ray creates a seat, a responsibility or a KPI, so this is new
// work). It reads a round's answers and report and suggests changes to the org chart:
//
// 1. a seat per area of handed-off work (the hire signal per pillar, ≥ 3 h a week): titled with
//    the engine's role for that pillar, reporting to the seat that hands off the most of it, its
//    responsibilities the handed-off tasks (similar names merged), and one weekly KPI: the hours
//    of that work it covers;
// 2. a seat for each guest respondent (someone without a seat yet), reporting where the round
//    said, its responsibilities the tasks they keep;
// 3. responsibilities on existing seats for the large tasks their holder keeps and the seat does
//    not name yet;
// 4. an owner for each crack several people named (a responsibility on the head seat, to move).
//
// Suggestions, not gospel (x-ray.md §8 rule 7): every proposal is reviewed by the owner, and
// nothing is written until accepted items are applied (API, `assessments.apply`).
import { getPillarLabel } from "../engine/pillars";
import type { Answers, RespondentInput } from "../questionnaire/questionnaire";
import { areaName, type RoundReport } from "../report/roundReport";
import { normalizeActivityTokens, tokensOverlap } from "../team/teamAggregation";

/** `teamAggregation.PILLAR_ROLE_SUGGESTION`, per pillar (the lifted map is module-private). */
export const ROLE_FOR_PILLAR: Record<string, string> = {
  "Operations & Delivery": "Operations Assistant",
  "Revenue & Sales": "Sales Support Assistant",
  "Growth & Brand": "Marketing Assistant",
  "Strategy & Culture": "Chief of Staff",
  "Non Strategic": "Virtual Assistant",
  "Administrative & Internal": "Virtual Assistant",
};

export type ProposalKind = "seat" | "responsibility" | "metric";

export type SeatProposal = {
  title: string;
  role_name: string;
  /** An existing seat it reports to; null with `reports_to_key` null: the top of the chart. */
  reports_to_seat_id: string | null;
  pillar: string | null;
  hours_per_week: number;
  source: "hire_signal" | "guest";
  participant_id: string | null;
};

export type ResponsibilityProposal = {
  /** An existing seat, or null when it goes on a proposed seat (`depends_on`). */
  seat_id: string | null;
  name: string;
  hours_per_week: number | null;
  pillar: string | null;
};

export type MetricProposal = {
  seat_id: string | null;
  key: string;
  name: string;
  unit: "number";
  direction: "gte";
  cadence: "weekly";
  target: number;
};

export type ProposalDraft =
  | BaseDraft<"seat", SeatProposal>
  | BaseDraft<"responsibility", ResponsibilityProposal>
  | BaseDraft<"metric", MetricProposal>;

type BaseDraft<K extends ProposalKind, P> = {
  key: string;
  kind: K;
  title: string;
  detail: string;
  /** The seat proposal this one hangs on (accepting it needs that seat accepted too). */
  depends_on: string | null;
  payload: P;
};

export type ChartSeat = {
  id: string;
  title: string;
  supervisor_seat_id: string | null;
  responsibilities: readonly string[];
};

export type TranslatorRespondent = RespondentInput & {
  kind: "member" | "guest";
  seat_id: string | null;
  reports_to_seat_id: string | null;
};

const r1 = (n: number) => Math.round(n * 10) / 10;
const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "item";

type Task = { name: string; hours: number; pillar: string | null; who: string };

/** Similar task names merged (the overlap rule), hours summed, largest first. */
export function mergeTasks(tasks: readonly Task[]): { name: string; hours: number }[] {
  const groups: { name: string; hours: number; tokens: string[] }[] = [];
  for (const t of [...tasks].sort((a, b) => b.hours - a.hours)) {
    const tokens = normalizeActivityTokens(t.name);
    const g = groups.find((x) => tokens.length > 0 && tokensOverlap(x.tokens, tokens));
    if (g) {
      g.hours += t.hours;
      if (t.name.length < g.name.length) g.name = t.name;
    } else groups.push({ name: t.name.trim(), hours: t.hours, tokens });
  }
  return groups
    .map((g) => ({ name: g.name, hours: r1(g.hours) }))
    .sort((a, b) => b.hours - a.hours);
}

function namedAlready(name: string, existing: readonly string[]): boolean {
  const tokens = normalizeActivityTokens(name);
  return existing.some((e) => {
    const et = normalizeActivityTokens(e);
    return (
      e.trim().toLowerCase() === name.trim().toLowerCase() ||
      (tokens.length > 0 && tokensOverlap(tokens, et))
    );
  });
}

function tasksOf(r: TranslatorRespondent, handedOff: boolean): Task[] {
  const a: Answers | null = r.completed ? r.answers : null;
  return (a?.tasks ?? [])
    .filter((t) => t.name.trim() && (t.hours_per_week ?? 0) > 0 && t.someone_else === handedOff)
    .map((t) => ({
      name: t.name.trim(),
      hours: Number(t.hours_per_week) || 0,
      pillar: t.pillar ? getPillarLabel(t.pillar) : null,
      who: r.participant_id,
    }));
}

export const HIRE_MIN_HOURS = 3;
export const KEEP_MIN_HOURS = 3;
const MAX_HIRE_SEATS = 3;
const MAX_RESPONSIBILITIES_PER_SEAT = 6;
const MAX_NEW_ON_EXISTING = 3;

export function proposeChart(input: {
  report: RoundReport;
  respondents: readonly TranslatorRespondent[];
  seats: readonly ChartSeat[];
  /** The seat at the top of the chart (the owner's), where unplaced things report. */
  head_seat_id: string | null;
}): ProposalDraft[] {
  const out: ProposalDraft[] = [];
  const seatsById = new Map(input.seats.map((s) => [s.id, s]));
  const done = input.respondents.filter((r) => r.completed && r.answers);

  // 1. Seats for handed-off work, per pillar.
  const handedOff = done.flatMap((r) => tasksOf(r, true));
  for (const p of input.report.hire_signal.by_pillar
    .filter((b) => b.hours_per_week >= HIRE_MIN_HOURS)
    .slice(0, MAX_HIRE_SEATS)) {
    const tasks = handedOff.filter((t) => (t.pillar ?? "Uncategorized") === p.pillar);
    if (tasks.length === 0) continue;
    const role = ROLE_FOR_PILLAR[p.pillar] ?? "Virtual Assistant";
    const hours = r1(tasks.reduce((s, t) => s + t.hours, 0));
    // It reports to the seat handing off the most of this work.
    const byWho = new Map<string, number>();
    for (const t of tasks) byWho.set(t.who, (byWho.get(t.who) ?? 0) + t.hours);
    const top = [...byWho.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    const from = done.find((r) => r.participant_id === top);
    const reportsTo = from?.seat_id ?? from?.reports_to_seat_id ?? input.head_seat_id;
    const existing = input.seats.find((s) => s.title.trim().toLowerCase() === role.toLowerCase());
    const seatKey = `seat:hire:${slug(p.pillar)}`;
    if (!existing)
      out.push({
        key: seatKey,
        kind: "seat",
        title: `New seat: ${role}`,
        detail: `${hours} h a week of ${areaName(p.pillar)} work people say someone else should do (${tasks.length} task${tasks.length === 1 ? "" : "s"}).`,
        depends_on: null,
        payload: {
          title: role,
          role_name: role,
          reports_to_seat_id: reportsTo ?? null,
          pillar: p.pillar,
          hours_per_week: hours,
          source: "hire_signal",
          participant_id: null,
        },
      });
    const merged = mergeTasks(tasks).slice(0, MAX_RESPONSIBILITIES_PER_SEAT);
    const already = existing?.responsibilities ?? [];
    for (const m of merged) {
      if (namedAlready(m.name, already)) continue;
      out.push({
        key: `resp:${existing ? existing.id : seatKey}:${slug(m.name)}`,
        kind: "responsibility",
        title: `${m.name} → ${existing ? existing.title : role}`,
        detail: `${m.hours} h a week handed off.`,
        depends_on: existing ? null : seatKey,
        payload: {
          seat_id: existing?.id ?? null,
          name: m.name,
          hours_per_week: m.hours,
          pillar: p.pillar,
        },
      });
    }
    out.push({
      key: `metric:${existing ? existing.id : seatKey}`,
      kind: "metric",
      title: `KPI: ${role} hours covered`,
      detail: `Weekly hours of handed-off ${areaName(p.pillar)} work the seat covers; target ${Math.round(hours)}.`,
      depends_on: existing ? null : seatKey,
      payload: {
        seat_id: existing?.id ?? null,
        key: `assess_${slug(role)}_hours`.slice(0, 60),
        name: `${role}: handed-off hours covered`,
        unit: "number",
        direction: "gte",
        cadence: "weekly",
        target: Math.max(1, Math.round(hours)),
      },
    });
  }

  // 2. A seat for each guest who answered (people the chart does not have yet).
  for (const r of done.filter((x) => x.kind === "guest" && !x.seat_id)) {
    const title = (r.answers?.role_title ?? r.role_title ?? "").trim() || `${r.name}'s seat`;
    const kept = mergeTasks(tasksOf(r, false)).filter((t) => t.hours >= 2);
    const seatKey = `seat:guest:${r.participant_id}`;
    out.push({
      key: seatKey,
      kind: "seat",
      title: `New seat: ${title} (${r.name})`,
      detail: `${r.name} answered as a guest; the chart has no seat for this work yet.`,
      depends_on: null,
      payload: {
        title,
        role_name: title,
        reports_to_seat_id: r.reports_to_seat_id ?? input.head_seat_id,
        pillar: null,
        hours_per_week: r1(kept.reduce((s, t) => s + t.hours, 0)),
        source: "guest",
        participant_id: r.participant_id,
      },
    });
    for (const t of kept.slice(0, 5))
      out.push({
        key: `resp:${seatKey}:${slug(t.name)}`,
        kind: "responsibility",
        title: `${t.name} → ${title}`,
        detail: `${t.hours} h a week, kept by ${r.name}.`,
        depends_on: seatKey,
        payload: { seat_id: null, name: t.name, hours_per_week: t.hours, pillar: null },
      });
  }

  // 3. Large kept tasks an existing seat does not name yet.
  for (const r of done.filter((x) => x.seat_id && seatsById.has(x.seat_id))) {
    const seat = seatsById.get(r.seat_id as string) as ChartSeat;
    const fresh = mergeTasks(tasksOf(r, false))
      .filter((t) => t.hours >= KEEP_MIN_HOURS && !namedAlready(t.name, seat.responsibilities))
      .slice(0, MAX_NEW_ON_EXISTING);
    for (const t of fresh) {
      const key = `resp:${seat.id}:${slug(t.name)}`;
      if (out.some((o) => o.key === key)) continue;
      out.push({
        key,
        kind: "responsibility",
        title: `${t.name} → ${seat.title}`,
        detail: `${r.name} spends ${t.hours} h a week on it; the seat does not list it.`,
        depends_on: null,
        payload: { seat_id: seat.id, name: t.name, hours_per_week: t.hours, pillar: null },
      });
    }
  }

  // 4. An owner for each crack several people named.
  const head = input.head_seat_id ? seatsById.get(input.head_seat_id) : undefined;
  if (head)
    for (const g of input.report.gaps.filter((x) => x.kind === "crack").slice(0, 2)) {
      const name = g.title.replace(/^"(.*)" falls through the cracks$/, "$1");
      const key = `resp:${head.id}:crack_${slug(name)}`;
      if (namedAlready(name, head.responsibilities) || out.some((o) => o.key === key)) continue;
      out.push({
        key,
        kind: "responsibility",
        title: `Own: ${name} → ${head.title}`,
        detail: `${g.detail} Accept it here, then move it to the seat that should own it.`,
        depends_on: null,
        payload: {
          seat_id: head.id,
          name: `Own: ${name}`.slice(0, 200),
          hours_per_week: null,
          pillar: null,
        },
      });
    }
  return out;
}
